/*
  Счета в окне выбора: флажок, номер с адресом, должник, лист файла и сумма.

  Ничего не грузит и выбора не хранит - всё приходит из `usePicker`. Сумма -
  та, что пришла в файле (заявленный долг или начислено); её назначение
  подписано в заголовке колонки, который даёт загрузка. Нет заголовка - нет и
  колонки: в домовой книге денег нет, и столбец прочерков ничего не говорит.
*/

import { money } from '../../api/format.js'
import { Check, Tag } from '../Common.jsx'
import PageBar from '../PageBar.jsx'
import usePaging from '../usePaging.js'

/*
  Должник из системы (20.10.2026, вариант Б макета «1-fio-B»): у загрузки
  начислений сервер называет, к кому в портфеле лягут начисления, а человек из
  файла стоит под ним жёлтой пометкой, только если он другой. У загрузок, где
  должника приносит сам файл (реестр, домовая книга), - имя из файла, как раньше.
*/
function Debtor({ choice }) {
  if (!choice.debtor_state) return choice.person ?? <span className="muted">-</span>
  const written = choice.person ? `в файле: ${choice.person}` : null
  if (choice.debtor_state === 'new') {
    return (
      <span className="muted">
        новый счёт{choice.person ? `, должник из файла: ${choice.person}` : ''}
      </span>
    )
  }
  if (choice.debtor_state === 'nobody') {
    return (
      <>
        <span className="muted">нет в системе</span>
        {written ? <div className="muted">{written}</div> : null}
      </>
    )
  }
  return (
    <>
      {choice.debtor}
      {choice.name_differs && written ? <div className="picker-written">{written}</div> : null}
    </>
  )
}

function State({ choice }) {
  if (choice.name_differs) return <Tag kind="warn">ФИО расходятся</Tag>
  if (choice.debtor_state === 'new') return <Tag kind="calm">новый счёт</Tag>
  if (choice.debtor_state === 'nobody') return <Tag kind="warn">должник не указан</Tag>
  return null
}

export default function PickerTable({ shown, picked, mark, amountTitle, filterKey }) {
  /*
    21.10.2026: общая панель страниц (замечание владельца). Листается ТОЛЬКО показ:
    отметки, флажок в шапке и «Отметить показанные» в подвале окна по-прежнему
    действуют на весь отбор (`shown`), а не на страницу: флажок в шапке и кнопки в
    подвале обязаны означать одно и то же, а подвал страницы не знает. Панель стоит
    внутри прокручиваемой области: снаружи ей нет места в сетке «дома и счета»
    (`picker-split`), не ломая раскладку.

    23.10.2026 (находка проверки): смена отбора (`filterKey` - раскладка, отбор, дом или
    лист, поиск) возвращает на первую страницу; прежде на шестой странице набирали фамилию и
    видели «31-40 из 40» - конец найденного вместо начала.
  */
  const { rows, bar } = usePaging(shown, undefined, filterKey)
  const marked = shown.filter((choice) => picked.has(choice.number)).length
  const fromSystem = shown.some((choice) => choice.debtor_state)
  if (shown.length === 0) {
    return <p className="muted">В этом отборе счетов нет. Смените отбор или очистите поиск.</p>
  }
  return (
    <div className="modal__scroll">
      <table className="table table--fixed table--picker">
        <thead>
          <tr>
            <th className="table__control">
              <Check
                checked={marked === shown.length}
                indeterminate={marked > 0 && marked < shown.length}
                onChange={(on) => mark(shown, on)}
                title="Отметить показанные счета"
              />
            </th>
            <th>Счёт</th>
            <th>{fromSystem ? 'Должник в системе' : 'Должник'}</th>
            <th>Из файла</th>
            {amountTitle ? <th className="table__num">{amountTitle}</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((choice) => (
            <tr key={choice.number}>
              <td className="table__control">
                <Check
                  checked={picked.has(choice.number)}
                  onChange={(on) => mark([choice], on)}
                  title={`Счёт ${choice.number}`}
                />
              </td>
              <td>
                <b>{choice.number}</b>
                {choice.address ? <div className="muted">{choice.address}</div> : null}
              </td>
              <td data-label="Должник">
                <Debtor choice={choice} />
              </td>
              <td className="table__block" data-label="Из файла">
                {(choice.sheets ?? []).map((sheet) => (
                  <div key={sheet.key} className="muted">
                    {sheet.title}
                  </div>
                ))}
                <State choice={choice} />
                {choice.attention ? <Tag kind="warn">{choice.note}</Tag> : (choice.note ?? null)}
              </td>
              {amountTitle ? (
                <td className="table__num" data-label={amountTitle}>
                  {choice.amount ? money(choice.amount) : <span className="muted">-</span>}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      <PageBar {...bar} what="счетов" />
    </div>
  )
}
